/**
 * Portal v2 alerts log (`/portal/alerts`).
 *
 * View-only — no mutations. SectionHeader with "X open alerts" subtitle +
 * filter pills (All / Unread / Read, role=radiogroup) + CardV2 activity
 * feed: severity-tinted icon row + title + message + meta line.
 *
 * Backend exposes severity (low|medium|high|critical) and is_read; map
 * severity → tone (critical→danger, high→warn, medium→info, low→neutral).
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  AlertCircle,
  AlertTriangle,
  Bell,
  CheckCircle2,
  Info,
} from "lucide-react";
import { Skeleton } from "@ddmarc/ui";
import { usePortalScope } from "@/lib/portal-scope-context";
import { portalApi, type PortalAlert } from "@/lib/portal-api";
import {
  CardV2,
  SectionHeader,
  StatPill,
  type StatPillTone,
} from "@/components/primitives";
import { cn } from "@/lib/utils";

type ReadFilter = "all" | "unread" | "read";

const FILTERS: Array<{ id: ReadFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "unread", label: "Unread" },
  { id: "read", label: "Read" },
];

type SeverityStyle = {
  tone: StatPillTone;
  icon: typeof AlertTriangle;
  varKey: string;
};

// Keyed by the backend's severity vocabulary (info | warning | critical), plus
// legacy low/medium/high aliases. Any unknown/future value falls back to
// DEFAULT_SEVERITY so the page renders instead of crashing on `undefined.icon`.
const SEVERITY: Record<string, SeverityStyle> = {
  critical: { tone: "danger", icon: AlertTriangle, varKey: "--v2-danger" },
  high: { tone: "danger", icon: AlertTriangle, varKey: "--v2-danger" },
  warning: { tone: "warn", icon: AlertCircle, varKey: "--v2-warn" },
  medium: { tone: "warn", icon: AlertCircle, varKey: "--v2-warn" },
  info: { tone: "info", icon: Info, varKey: "--v2-info" },
  low: { tone: "neutral", icon: CheckCircle2, varKey: "--v2-good" },
};

const DEFAULT_SEVERITY: SeverityStyle = {
  tone: "neutral",
  icon: Info,
  varKey: "--v2-info",
};

export function PortalAlertsV2Page() {
  const [filter, setFilter] = useState<ReadFilter>("all");

  const { selectedDomainId } = usePortalScope();
  const { data, isLoading } = useQuery({
    queryKey: ["portal", "alerts", selectedDomainId],
    queryFn: () =>
      portalApi.listAlerts({ size: 50, domain_id: selectedDomainId }),
  });

  // Memoised so the `?? []` fallback does not produce a fresh array (and thus
  // a changed dependency) on every render.
  const items = useMemo<PortalAlert[]>(() => data?.items ?? [], [data]);
  const unreadCount = data?.unread_count ?? 0;

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((a) => (filter === "unread" ? !a.is_read : a.is_read));
  }, [items, filter]);

  return (
    <div className="space-y-3.5">
      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Alerts
          </span>
        }
        subtitle={
          unreadCount > 0
            ? `${unreadCount.toLocaleString()} open · stay informed about important events.`
            : "All caught up. Alerts will surface here when issues arise."
        }
      />

      <div
        role="radiogroup"
        aria-label="Filter alerts by read status"
        className="flex items-center gap-1.5"
      >
        {FILTERS.map((f) => {
          const active = f.id === filter;
          return (
            <button
              key={f.id}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setFilter(f.id)}
              className={cn(
                "rounded-full px-3 py-1 text-[12px] font-medium transition-colors",
                active
                  ? "bg-foreground text-background"
                  : "bg-muted text-muted-foreground hover:bg-secondary hover:text-foreground"
              )}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      <CardV2 bodyClassName="p-0">
        {isLoading ? (
          <div className="space-y-3 p-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <Bell className="h-10 w-10 text-[var(--v2-fg-faint)]" />
            <p className="text-[13px] font-medium text-foreground">
              {filter === "all" ? "No alerts" : `No ${filter} alerts`}
            </p>
            <p className="text-[12.5px] text-muted-foreground">
              {filter === "all"
                ? "You're all caught up."
                : "Try a different filter."}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {filtered.map((a) => {
              const sev = SEVERITY[a.severity] ?? DEFAULT_SEVERITY;
              const Icon = sev.icon;
              return (
                <li
                  key={a.id}
                  className={cn(
                    "flex gap-3 px-4 py-3.5",
                    a.is_read && "opacity-70"
                  )}
                >
                  <span
                    aria-hidden
                    className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                    style={{ backgroundColor: `var(${sev.varKey}-soft)` }}
                  >
                    <Icon
                      className="h-4 w-4"
                      style={{ color: `var(${sev.varKey})` }}
                    />
                  </span>
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-[13px] font-medium text-foreground">
                        {a.title}
                      </p>
                      <StatPill tone={sev.tone}>{a.severity}</StatPill>
                    </div>
                    <p className="text-[12.5px] text-muted-foreground">
                      {a.message}
                    </p>
                    <div className="flex items-center gap-3 text-[11.5px] text-[var(--v2-fg-faint)]">
                      {a.domain && (
                        <span className="font-mono-data">{a.domain}</span>
                      )}
                      <span>
                        {format(new Date(a.created_at), "MMM d, yyyy h:mm a")}
                      </span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardV2>
    </div>
  );
}
